import "./App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import CometPixel from "./components/CometPixel";
import { initPostHog } from "./services/posthog";
import { Provider } from "react-redux";
import { ClerkProvider } from "@clerk/clerk-react";
import RootStore from "./store/RootStore";
import { WebSocketProvider, WebSocketLocationBridge } from "./WebsocketProvider.js";
import PostAuth from "./pages/auth-page/PostAuth";
import * as Sentry from "@sentry/react";
import UnderMaintenance from "./pages/UnderMaintenance";
import { ClerkEventTracker } from "./utils/ClerkEventTracker";
import { useAnalyticsInit } from "./hooks/useAnalyticsInit";
import AppVersionMonitor from "./components/version/AppVersionMonitor";
import ForceRefreshModal from "./components/version/ForceRefreshModal";
import SoftUpdateBanner from "./components/version/SoftUpdateBanner";
import ServiceOutageBanner from "./components/ServiceOutageBanner";
import PaymentRecoveryMonitor from "./components/payment/PaymentRecoveryMonitor";
import CrossTabSyncBridge from "./components/CrossTabSyncBridge";
import BaseLayout from "./pages/layouts/BaseLayout";
import InhouseLoader from "./components/InhouseLoader/InhouseLoader";
import { isClaudeConnectorEnabled } from "./utils/claudeConnectorFlag";

// Lazy-loaded modal components (only rendered on user interaction)
const CheckoutFlowModal = lazy(() => import("./pages/PaymentModals/CheckoutFlowModal"));
const DowngradeConfirmModal = lazy(() => import("./pages/PaymentModals/DowngradeConfirmModal"));
const DowngradeSuccessModal = lazy(() => import("./pages/PaymentModals/DowngradeSuccessModal"));
const CancelConfirmModal = lazy(() => import("./pages/PaymentModals/CancelConfirmModal"));
const CancelSuccessModal = lazy(() => import("./pages/PaymentModals/CancelSuccessModal"));
const RestartConfirmModal = lazy(() => import("./pages/PaymentModals/RestartConfirmModal"));
const RestartSuccessModal = lazy(() => import("./pages/PaymentModals/RestartSuccessModal"));
const AccountModalWrapper = lazy(() => import("./pages/AccountModal/AccountModalWrapper"));
const FreeCallNudgeModal = lazy(() => import("./pages/PaymentModals/FreeCallNudgeModal"));

// Lazy-loaded route components
const Homepage = lazy(() => import("./pages/home-page/Home"));
const NotFoundPage = lazy(() => import("./pages/not-found-page"));
const SharedChatPage = lazy(() => import("./pages/secured-share-page"));
const ChatPageComponent = lazy(() => import("./pages/chat-page/ChatPageComponent"));
const Signin = lazy(() => import("./pages/auth-page/Signin"));
const Signup = lazy(() => import("./pages/auth-page/Signup"));
const TryPage = lazy(() => import("./pages/try-page"));
const DocLibrary = lazy(() => import("./pages/document-library/DocLibrary"));
const DocLibraryDesign = lazy(() => import("./pages/document-library/DocLibraryDesign"));
const PaymentReturnPage = lazy(() => import("./pages/payment-status/PaymentReturnPage"));
const InternalManualChargePage = lazy(() => import("./pages/internal-manual-charge"));
const InternalEnableMessagingPage = lazy(() => import("./pages/internal-enable-messaging"));
const ConsultationHandoffPage = lazy(() => import("./pages/consultation-handoff/ConsultationHandoffPage"));
const ConnectorChatPage = lazy(() => import("./pages/connector-chat/ConnectorChatPage"));
const MarketingRouter = lazy(() => import("./marketing/routes"));

// Blank screen for Suspense fallback — used for routes without BaseLayout
function PageLoader() {
  return (
    <div style={{ height: "100vh", backgroundColor: "#FDFBF8" }} />
  );
}

// Layout route: renders BaseLayout eagerly so the shell (sidenav/header) stays
// visible while lazy page content loads inside the content area.
function BaseLayoutRoute() {
  return (
    <BaseLayout>
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </BaseLayout>
  );
}

// Initialize Chargebee once at module load — before any component renders
if (window.Chargebee) {
  window.Chargebee.init({
    site: process.env.REACT_APP_CHARGEBEE_SITE,
    publishableKey: process.env.REACT_APP_CHARGEBEE_PUBLISHABLE_KEY,
  });
}

// Initialize PostHog once at module load
initPostHog();

// Create Sentry-wrapped Routes once at module level
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function App() {
  // Initialize analytics with proper inhouse_anonymous_id before any events fire
  useAnalyticsInit();
  const PUBLISHABLE_KEY = process.env.REACT_APP_VITE_CLERK_PUBLISHABLE_KEY;
  const updating = process.env.REACT_APP_WEBSITE_UNDER_MAINTENANCE === "true";

  const localization = RootStore.getState().onboarding.clerkLocalization;

  return (
    <>
      <ClerkProvider
        localization={localization}
        publishableKey={PUBLISHABLE_KEY}
        afterSignOutUrl="/try"
        phoneDefaultCountry="US"
        appearance={{
          variables: {
            colorPrimary: "#1B2B48",
            colorBackground: "white",
          },
          elements: {
            headerTitle: "!text-[24px] md:!text-[28px] text-[#1B2B48] mb-1",
            rootBox: "m-0",
            cardBox: {
              boxShadow: "none",
              height: "100%",
            },
          },
        }}
      >
        <ClerkEventTracker>
          <Provider store={RootStore}>
            <CrossTabSyncBridge />
            <AppVersionMonitor />
            <PaymentRecoveryMonitor />
            <ForceRefreshModal />
            <div className="fixed top-0 left-0 right-0 z-[9000]">
              <ServiceOutageBanner />
              <SoftUpdateBanner />
            </div>
            {/* <WebSocketProvider url={process.env.REACT_APP_WEBSOCKET_URL}> */}
            <WebSocketProvider>
              <Router>
                <CometPixel />
                <WebSocketLocationBridge />
                <Suspense fallback={null}>
                  <CheckoutFlowModal />
                  <DowngradeConfirmModal />
                  <DowngradeSuccessModal />
                  <CancelConfirmModal />
                  <CancelSuccessModal />
                  <RestartConfirmModal />
                  <RestartSuccessModal />
                  <FreeCallNudgeModal />
                  <AccountModalWrapper />
                </Suspense>
                <SentryRoutes>
                  {updating && !localStorage.getItem("testTeam") ? (
                    <Route path="*" element={<UnderMaintenance />} />
                  ) : (
                    <>
                      {/* Routes WITHOUT BaseLayout — outer Suspense catches these */}
                      <Route path="/post-auth" element={<PostAuth />} />
                      <Route path="/" element={<Navigate to="/home" />} />
                      <Route path="/payment/3ds-return" element={<Suspense fallback={<PageLoader />}><PaymentReturnPage /></Suspense>} />

                      {/* Deprecated checkout/payment-status routes */}
                      <Route path="/subscription/checkout/:transactionId" element={<Navigate to="/home?expired=1" replace />} />
                      <Route path="/consultation/checkout/:transactionId" element={<Navigate to="/home?expired=1" replace />} />
                      <Route path="/doc-unlocking/checkout/:transactionId" element={<Navigate to="/home?expired=1" replace />} />
                      <Route path="/finalization/checkout/:transactionId" element={<Navigate to="/home?expired=1" replace />} />
                      <Route path="/bundle-doc-consultation/checkout/:transactionId" element={<Navigate to="/home?expired=1" replace />} />
                      <Route path="/bundle-doc-finalization/checkout/:transactionId" element={<Navigate to="/home?expired=1" replace />} />
                      <Route path="/subscription/payment-status/:transactionId" element={<Navigate to="/home?expired=1" replace />} />
                      <Route path="/consultation/payment-status/:transactionId" element={<Navigate to="/home?expired=1" replace />} />
                      <Route path="/doc-unlocking/payment-status/:transactionId" element={<Navigate to="/home?expired=1" replace />} />
                      <Route path="/finalization/payment-status/:transactionId" element={<Navigate to="/home?expired=1" replace />} />
                      <Route path="/bundle-doc-consultation/payment-status/:transactionId" element={<Navigate to="/home?expired=1" replace />} />
                      <Route path="/bundle-doc-finalization/payment-status/:transactionId" element={<Navigate to="/home?expired=1" replace />} />

                      <Route path="/signin" element={<Suspense fallback={<PageLoader />}><Signin /></Suspense>} />
                      <Route path="/signup" element={<Suspense fallback={<PageLoader />}><Signup /></Suspense>} />
                      <Route path="/try" element={<Suspense fallback={<PageLoader />}><TryPage /></Suspense>} />
                      <Route path="/lp/*" element={<Suspense fallback={<PageLoader />}><MarketingRouter /></Suspense>} />

                      {/* Routes WITH BaseLayout — layout stays visible, only content suspends */}
                      <Route element={<BaseLayoutRoute />}>
                        <Route path="/home" element={<Homepage />} />
                        <Route path="/document-library" element={<DocLibrary />} />
                        <Route path="/document-library-design" element={<DocLibraryDesign />} />
                        <Route path="/chat" element={<ChatPageComponent />} />
                        <Route path="/chat/:chatId" element={<ChatPageComponent />} />
                        <Route path="/chat/shared/:chatId" element={<SharedChatPage />} />
                      </Route>

                      {/* Routes WITHOUT BaseLayout */}
                      <Route path="/not-found" element={<Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>} />
                      <Route path="/internal-manual-charge" element={<Suspense fallback={<PageLoader />}><InternalManualChargePage /></Suspense>} />
                      <Route path="/internal-enable-messaging" element={<Suspense fallback={<PageLoader />}><InternalEnableMessagingPage /></Suspense>} />
                      {/* Connector hand-off (INH-5704): outside BaseLayout, so no app chrome wraps the confidentiality boundary and signed-out visitors keep their return path.
                          Behind REACT_APP_CLAUDE_CONNECTOR_ENABLED (src/utils/claudeConnectorFlag.ts): off, the path is not registered and falls through to /not-found below. */}
                      {isClaudeConnectorEnabled() && (
                        <Route path="/consultation/claude/:handoffId" element={<Suspense fallback={<PageLoader />}><ConsultationHandoffPage /></Suspense>} />
                      )}
                      {/* Connector chat request (INH-5831): the ask_inhouse link; same placement and flag as the hand-off above. */}
                      {isClaudeConnectorEnabled() && (
                        <Route path="/chat/connector/:requestId" element={<Suspense fallback={<PageLoader />}><ConnectorChatPage /></Suspense>} />
                      )}
                      <Route path="*" element={<Navigate to="/not-found" />} />
                    </>
                  )}
                </SentryRoutes>
              </Router>
            </WebSocketProvider>
          </Provider>
        </ClerkEventTracker>
      </ClerkProvider>
    </>
  );
}

export default App;
